/**
 * PayoutPeek — "what does each square pay?", for three seconds.
 *
 * ─── THE PROBLEM IT SOLVES ───────────────────────────────────────────────────
 *
 * Owner: "users do not get to know how much are they going to be earning in
 * betting, which block how much." He is right, and it is the single biggest
 * information gap on the table. A roulette board is the only betting surface in
 * the game that never states its own prices: the numbers are ON it, the odds are
 * in the player's head or nowhere. A new player literally cannot tell that the
 * corner between four numbers pays 8 and the line below pays 5.
 *
 * ─── WHY IT IS AN OVERLAY AND NOT A SECOND TABLE ─────────────────────────────
 *
 * The obvious build is a modal showing a diagram of the board. That is the wrong
 * one: a drawn copy is a second source of truth for the geometry, and the moment
 * the real board's proportions change the diagram lies about where to tap. Worse,
 * it teaches a layout the player then has to re-find on the real felt.
 *
 * So this paints ON the real board, positioned from the SAME lattice the chips
 * are placed with — same origin, same cell pitch, same rails. If the board moves,
 * this moves with it, because it is not a copy of the board, it is a reading of
 * it. Everything else on the table is hidden for the duration, which is what the
 * owner asked for and is also just good sense: the whole point is to look at one
 * thing without fifty crowd chips on top of it.
 *
 * ─── WHERE THE NUMBERS COME FROM ─────────────────────────────────────────────
 *
 * `PAYOUT` in engine.ts — the exact table settlement pays from, itself derived as
 * `36/covered − 1`. Not a list typed out here. A payout preview that can disagree
 * with the payout is worse than no preview at all: it is the house quoting a
 * price it will not honour, which is the one mistake a betting product must never
 * make. Deriving it means the two cannot drift even if the odds are ever changed.
 */

import { useEffect, useRef } from 'react';
import { GAP } from './Board';
import { press } from './press';
import { PAYOUT, RETURN_X } from './engine';
import { pitchX, pitchY, type Lattice } from './lattice';

/** How long the felt stays bare. Owner's number, and it is a good one: long
 *  enough to read a column, short enough that nobody feels held up mid-round. */
/* 4 seconds: the owner asked 3-5; the board carries more information now (the
   line-bet legend moved into the header), so the longer end reads better. */
export const PEEK_MS = 4000;

/** `x to 1`, written the way a player reads it. */
/* X-first AND what-you-GET. X2 on red means a winning ₹100 puts ₹200 in your
   wallet. Profit-speak ("X1") told an even-money player they would break even —
   the owner is right that no real table talks like that. */
const mult = (k: keyof typeof PAYOUT) => `X${RETURN_X[k]}`;

export function PayoutPeek({ lattice, railL, railR, onDone }: {
  lattice: Lattice | null;
  railL: number;
  railR: number;
  onDone: () => void;
}) {
  /* THE TIMER IS ARMED ONCE, AND THE CALLBACK IS READ THROUGH A REF.
   *
   * The first version had `[onDone]` as the dependency. `onDone` is an inline
   * arrow in Page, so it is a new function on every render — and Page re-renders
   * once a second because the countdown clock is state. So the effect tore the
   * timer down and re-armed it every second and the three seconds never elapsed:
   * the peek simply stayed up forever.
   *
   * Keeping the latest callback in a ref and depending on NOTHING means the timer
   * survives any amount of parent re-rendering. That is the right shape for
   * anything self-dismissing, regardless of how carefully the caller memoises —
   * a component that closes itself should not be able to be held open by an
   * unrelated re-render somewhere above it. */
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    const t = window.setTimeout(() => done.current(), PEEK_MS);
    return () => window.clearTimeout(t);
  }, []);

  if (!lattice) return null;
  const L = lattice;
  const PX = pitchX(L), PY = pitchY(L);
  const numbersW = 3 * L.cellW + 2 * L.gap;
  // Same origin as Overlay.tsx and the chip layer: lattice x=0 is number column
  // zero, which is one whole rail in from the grid's left edge.
  const originX = railL + GAP;

  /** A label centred on a cell of the number grid. */
  const cell = (r: number, c: number, text: string, key: string) => (
    <span key={key} className="rlt-peek-n"
          style={{ left: originX + c * PX + L.cellW / 2, top: r * PY + L.cellH / 2,
                   width: L.cellW * 0.94, height: L.cellH * 0.8 }}>
      {text}
    </span>
  );

  const items: React.ReactNode[] = [];

  // ── the 37 numbers. Zero spans all three columns on row 0. ──
  items.push(
    /* The zero sits LOW in its cell: the title bar owns the top of the board and
       the two were printing over each other. */
    <span key="z" className="rlt-peek-n"
          style={{ left: originX + numbersW / 2, top: L.cellH * 0.74,
                   width: numbersW * 0.5, height: L.cellH * 0.5 }}>
      {mult('straight')}
    </span>,
  );
  for (let n = 1; n <= 36; n++) {
    items.push(cell(Math.floor((n - 1) / 3) + 1, (n - 1) % 3, mult('straight'), `n${n}`));
  }

  // ── the LEFT rail: 1~18, EVEN, red, black, ODD, 19~36 — six even-money bets,
  //    each two rows tall, so each label sits between its pair of rows. ──
  const evens: [number, keyof typeof PAYOUT][] = [
    [1, 'low'], [3, 'even'], [5, 'red'], [7, 'black'], [9, 'odd'], [11, 'high'],
  ];
  for (const [row, kind] of evens) {
    items.push(
      <span key={`L${row}`} className="rlt-peek-r"
            style={{ left: railL / 2, top: (row + 0.5) * PY, width: railL * 0.86 }}>
        {mult(kind)}
      </span>,
    );
  }

  // ── the RIGHT rail: the three dozens, four rows each. ──
  for (let d = 0; d < 3; d++) {
    items.push(
      <span key={`D${d}`} className="rlt-peek-r"
            style={{ left: originX + numbersW + GAP + railR / 2,
                     top: (1 + d * 4 + 2) * PY - PY / 2, width: railR * 0.86 }}>
        {mult('dozen')}
      </span>,
    );
  }

  // ── the 2:1 strip under the board — the three columns. ──
  for (let c = 0; c < 3; c++) {
    items.push(
      <span key={`C${c}`} className="rlt-peek-r"
            style={{ left: originX + c * PX + L.cellW / 2, top: 13 * PY + L.cellH / 2,
                     width: L.cellW * 0.9 }}>
        {mult('column')}
      </span>,
    );
  }

  return (
    /* Tappable, and NOT just for convenience: this sits over the live betting
       board inside .rlt-feltinner, whose pointer handlers place chips. Without
       swallowing the press here a tap "on the prices" would land a bet on the
       square underneath it. So the whole panel closes on touch, and the X is the
       visible affordance for it. */
    <div className="rlt-peek" aria-label="What each bet pays" {...press(onDone)}>
      <button type="button" className="rlt-peek-x" aria-label="Close"
              {...press(onDone)}>
        <svg viewBox="0 0 24 24" aria-hidden>
          <path d="M18.3 5.7 12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7l1.4-1.4 6.3 6.3 6.3-6.3z" />
        </svg>
      </button>
      {/* The three seconds, made visible. "It did not close" and "it closed too
          soon" are the same complaint about an invisible timer; a hairline that
          runs out says which is happening. */}
      <span className="rlt-peek-bar" aria-hidden />
      {/* One line on its own bar. Two lines pushed into the zero row and the
          board is too tall to give away a whole row to a heading. */}
      {/* The line-bet legend lives UP HERE now: parked at the bottom it sat on
          top of the 2~1 column row, which is exactly the row the owner found
          unreadable. The header column has the room; the bottom strip does
          not. */}
      <div className="rlt-peek-head">
        <b>WHAT EACH SQUARE PAYS</b>
        <span>win = you get your bet × this</span>
        <i className="rlt-peek-lines">
          {mult('split')} line of 2 · {mult('street')} row of 3 · {mult('corner')} corner of 4 · {mult('sixline')} two rows
        </i>
      </div>
      {items}
    </div>
  );
}
